import { faPen, faPlaneDeparture } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography, RadioGroup, FormControlLabel, Radio, CircularProgress } from '@vapor/material'
import { useConfirm } from 'hic-components'
import { useCallback, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CreateCoParameter } from 'src/app/pages/accounting/documents/checkout-document/checkout-document-form'
import { BillF, ReservationF, DepositF } from '../../../types/reservation-detail.types'
import useCheckOutApi from './check-out.api'
import { OperationIfGranted } from 'src/app/components/authorization/operation-if-granted'
import { hicOperationsPermissions, HicOperations } from 'src/app/components/authorization/pages-permissions'
import { useHotelDate } from 'src/app/hooks/context/context.hook'
import { ReservedResourcePhase } from 'src/api/api.generated'

type CheckOutProps = {
  readOnly: boolean
  reservation: ReservationF
  bills: BillF[]
  deposits: DepositF[]
  checkOutReservedResourceIds: string[]
  onCreateDocument: (params: CreateCoParameter) => void
  isTimeResource?: boolean
}

export default function CheckOut({ reservation, bills, deposits, checkOutReservedResourceIds, onCreateDocument, readOnly, isTimeResource }: CheckOutProps) {
  const { t } = useTranslation()
  const { validateCheckOut, checkOut, changeReservedResourceDeparture, loading } = useCheckOutApi()

  const selectedRRAreTimeResourceType = useMemo(() => {
    return checkOutReservedResourceIds.every((selectedRRId) =>
      reservation?.reservedResources?.find(
        (rr) =>
          rr.id === selectedRRId &&
          rr.resourceOccupancies &&
          rr.resourceOccupancies.length > 0 &&
          rr.resourceOccupancies[0].reservedResourceType.timeConfiguration &&
          rr.phase === ReservedResourcePhase.CHECKIN,
      ),
    )
  }, [isTimeResource, checkOutReservedResourceIds, reservation])

  const confirm = useConfirm()
  // State:
  const [open, setOpen] = useState<boolean>(false)
  const [openEarlyCheckout, setOpenEarlyCheckout] = useState<boolean>(false)
  const [errors, setErrors] = useState<string[]>([])
  const [earlyDepartureData, setEarlyDepartureData] = useState<{ billIds: string[]; depositDocumentIds: string[]; productionAssignedIds: string[] }>()
  const hotelDate = useHotelDate()

  const onClose = () => {
    setOpen(false)
  }

  const onClick = useCallback(() => {
    const validationResponse = validateCheckOut(reservation, bills, deposits, checkOutReservedResourceIds)
    switch (validationResponse.nextAction) {
      case 'ERROR':
        setErrors(validationResponse.errors)
        setOpen(true)
        break
      case 'CLOSE_BILLS':
        onCreateDocument({
          billIds: validationResponse?.bills?.map((b) => b?.id) ?? [],
          depositDocumentIds: validationResponse.deposits?.map((d) => d.id) ?? [],
          productionAssignedIds: validationResponse.productionsAssigned?.map((pa) => pa.id) ?? [],
        })
        break
      case 'CONFIRM_EARLY_DEPARTURE':
        confirm({
          title: t('pms.booking.reservation.early_check_out_confirm_title'),
          message: t('pms.booking.reservation.early_check_out_confirm_description'),
          confirmButtonText: t('commons.ok'),
          cancelButtonText: t('commons.cancel'),
          onConfirm: () => {
            changeReservedResourceDeparture(reservation.tenantId, reservation.reservedResources?.filter((rr) => checkOutReservedResourceIds.includes(rr.id)) ?? [], hotelDate, () => {
              checkOut(reservation.tenantId, reservation.propertyId[0], reservation.id, checkOutReservedResourceIds)
            })
          },
        })
        break
      case 'CHECKOUT':
        checkOut(reservation.tenantId, reservation.propertyId[0], reservation.id, checkOutReservedResourceIds)
        break
      case 'EARLY_DEPARTURE':
        setOpenEarlyCheckout(true)
        setEarlyDepartureData({
          billIds: validationResponse?.bills?.map((b) => b?.id) ?? [],
          depositDocumentIds: validationResponse.deposits?.map((d) => d.id) ?? [],
          productionAssignedIds: validationResponse.productionsAssigned?.map((pa) => pa.id) ?? [],
        })
        break
    }
  }, [onCreateDocument, reservation, checkOutReservedResourceIds, bills, deposits])

  const earlyCheckoutNextAction = useCallback(
    async (nextAction: EarlyCheckoutNextActionType) => {
      if (earlyDepartureData) {
        switch (nextAction) {
          case 'CLOSE_BILLS':
            onCreateDocument({ ...earlyDepartureData })
            break
          case 'MODIFY_DEPARTURE':
            {
              const response = await changeReservedResourceDeparture(
                reservation.tenantId,
                reservation.reservedResources?.filter((rr) => checkOutReservedResourceIds.includes(rr.id)) ?? [],
                hotelDate,
                () => {
                  onCreateDocument({ ...earlyDepartureData })
                },
              )
            }
            break
        }
      }
      setOpenEarlyCheckout(false)
    },
    [reservation, onCreateDocument, earlyDepartureData, checkOutReservedResourceIds, hotelDate, onClick, loading],
  )

  return (
    <>
      <OperationIfGranted
        {...hicOperationsPermissions.get(HicOperations.CHECKOUT)}
        childrenWithoutPermissions={
          <Button variant="outlined" size="small" disabled={true} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', witeSpace: 'nowrap' }}>
            <FontAwesomeIcon icon={faPlaneDeparture} style={{ marginRight: 2 }} />
            <span style={{ marginLeft: 2 }}>{t('pms.booking.reservation.action.check_out')}</span>
          </Button>
        }
      >
        <Button
          variant="outlined"
          size="small"
          onClick={onClick}
          disabled={(!isTimeResource ? reservation.checkOutDisabled : !selectedRRAreTimeResourceType) || readOnly || loading}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', witeSpace: 'nowrap' }}
        >
          <FontAwesomeIcon icon={faPlaneDeparture} style={{ marginRight: 2 }} />
          <span style={{ marginLeft: 2 }}>{t('pms.booking.reservation.action.check_out')}</span>
        </Button>
      </OperationIfGranted>
      <CheckOutValidationDialog open={open} onClose={onClose} errors={errors} />
      <EarlyCheckoutDialog
        open={openEarlyCheckout}
        onClose={() => {
          setOpenEarlyCheckout(false)
        }}
        onConfirm={earlyCheckoutNextAction}
        loading={loading}
      />
    </>
  )
}

function CheckOutValidationDialog(props: { open: boolean; onClose: () => void; errors: string[] }) {
  const { t } = useTranslation()
  const { open, onClose, errors } = props
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t('pms.booking.reservation.check_out_error')}</DialogTitle>
      <DialogContent>
        {errors.map((e) => (
          <div key={e}>{e}</div>
        ))}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          {t('commons.ok')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

type EarlyCheckoutNextActionType = 'CLOSE_BILLS' | 'MODIFY_DEPARTURE'

function EarlyCheckoutDialog(props: { open: boolean; onClose: () => void; onConfirm: (nextAction: EarlyCheckoutNextActionType) => void; loading: boolean }) {
  const { t } = useTranslation()
  const { open, onClose, onConfirm, loading } = props
  const [nextAction, setNextAction] = useState<EarlyCheckoutNextActionType>()

  const confirmClick = useCallback(() => {
    if (nextAction) {
      onConfirm(nextAction)
    }
  }, [onConfirm, nextAction])

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>{t('pms.booking.reservation.early_checkout.title')}</DialogTitle>
        <DialogContent>
          <Stack>
            <Typography variant="body">{t('pms.booking.reservation.early_checkout.description')}</Typography>
            <RadioGroup
              onChange={(e) => {
                setNextAction(e.target.value as EarlyCheckoutNextActionType)
              }}
            >
              <FormControlLabel disabled={loading} value={'CLOSE_BILLS'} control={<Radio />} label={t('pms.booking.reservation.early_checkout.close_bills')} />
              <FormControlLabel disabled={loading} value={'MODIFY_DEPARTURE'} control={<Radio />} label={t('pms.booking.reservation.early_checkout.modify_departure')} />
            </RadioGroup>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => onClose()} disabled={loading}>
            {t('commons.cancel')}
          </Button>
          <Button variant="contained" onClick={confirmClick} disabled={loading} endIcon={loading ? <CircularProgress size={20} /> : undefined}>
            {t('commons.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
